import { useState, useMemo, useContext, createContext } from 'react'
import Dialog from '@mui/material/Dialog'
import useCallbackDebounced from '../../../hooks/shared/useCallbackDebounced'
import DialogSlideRightToLeftTransition from '../../atoms/DialogSlideRightToLeftTransition'
import { EditPostcodeDistrictsPanel } from '../../features/postcodeDistricts/EditPostcodeDistrictsPanel'
import { useOverlayPageNavigateBack } from '../../../hooks/navigation/usePersistedBackgroundLocation'

const PostcodeDistrictsDialogContext = createContext<{
  openPostcodeDistrictsDialog: () => void
  closePostcodeDistrictsDialog: () => void
} | null>(null)

const usePostcodeDistrictsDialog = () => {
  const context = useContext(PostcodeDistrictsDialogContext)
  if (!context) {
    throw new Error(
      'no PostcodeDistrictsDialogProvider found when calling usePostcodeDistrictsDialog()',
    )
  }
  return context
}

const PostcodeDistrictsDialogProvider = ({ children }) => {
  const overlayPageNavigateBack = useOverlayPageNavigateBack()
  const [open, setOpen] = useState(false)
  const openPostcodeDistrictsDialog = useCallbackDebounced(() => {
    setOpen(true)
  }, [])
  const closePostcodeDistrictsDialog = useCallbackDebounced(() => {
    setOpen(false)
  }, [])
  const onClose = useCallbackDebounced(() => {
    overlayPageNavigateBack()
  }, [overlayPageNavigateBack])
  const value = useMemo(
    () => ({
      openPostcodeDistrictsDialog,
      closePostcodeDistrictsDialog,
    }),
    [closePostcodeDistrictsDialog, openPostcodeDistrictsDialog],
  )
  return (
    <PostcodeDistrictsDialogContext.Provider value={value}>
      {children}
      <Dialog
        open={open}
        slots={{
          transition: DialogSlideRightToLeftTransition,
        }}
        onClose={onClose}
        sx={theme => ({
          '& .MuiDialog-paper': {
            maxWidth: '70vw',
            height: '100vh',
            width: 1000,
            position: 'fixed',
            top: 0,
            right: 0,
            margin: 0,
            maxHeight: 'none',
            borderRadius: 0,
            backgroundColor: 'pageBackground',
          },
          [theme.breakpoints.down('sm')]: {
            '& .MuiDialog-paper': {
              maxWidth: '100vw',
              width: '100vw',
              height: '100vh',
            },
          },
        })}
      >
        <EditPostcodeDistrictsPanel onSave={onClose} />
      </Dialog>
    </PostcodeDistrictsDialogContext.Provider>
  )
}

export { usePostcodeDistrictsDialog, PostcodeDistrictsDialogProvider }
export default PostcodeDistrictsDialogProvider
